import {createSlice} from '@reduxjs/toolkit'

import fetchCustomerInfo from '../thunks/fetchCustomerInfo'
import generateTestInfo from '../thunks/generateTestInfo'
import fetchOrder from '../thunks/fetchOrder'
import addOrder from '../thunks/addOrder'

const initialState = {
  // Data from server and for submission
  data: {
    email: '',
    optout: '0',
    phone: '',
    bill_fname: '',
    bill_lname: '',
    bill_organization: '',
    bill_country: '',
    bill_address1: '',
    bill_address2: '',
    bill_city: '',
    bill_state: '',
    bill_zipcode: '',
    shipping_same: true,
    ship_fname: '',
    ship_lname: '',
    ship_organization: '',
    ship_address1: '',
    ship_address2: '',
    ship_city: '',
    ship_country: '',
    ship_state: '',
    ship_zipcode: ''
  },
  // Status and error
  status: '',
  error: null,
  // Local state
  countryOptions: [],
  billCountry: null,
  billStateOptions: [],
  shipCountry: null,
  shipStateOptions: [],
  previousPhone: null
}

const customerInfoSlice = createSlice({
  name: 'customerInfo',
  initialState,
  reducers: {
    changeCustomerInfoStatus(state, action) {
      state.status = action.payload
    },
    changeCustomerInfo(state, action) {
      const {property, value} = action.payload
      state.data[property] = value

      if(property.indexOf('bill') > -1) {
        state.data[property] = value
        const shipProperty = `ship${property.substr(4)}`

        if(state.data.shipping_same && state.data.hasOwnProperty(shipProperty)) {
          state.data[shipProperty] = value
        }
      }
    },
    billToShip(state) {
      state.data.shipping_same = true

      Object.keys(state.data).map((dataProperty) => {
        if(dataProperty !== 'shipping_same' && dataProperty.indexOf('ship') > -1) {
          const billProperty = `bill${dataProperty.substr(4)}`

          if(state.data[billProperty]) {
            state.data[dataProperty] = state.data[billProperty]
          } else {
            state.data[dataProperty] = initialState.data[dataProperty]
          }
        }
      })
    },
    shipToBill(state) {
      state.data.shipping_same = true

      Object.keys(state.data).map((dataProperty) => {
        if(dataProperty !== 'shipping_same' && dataProperty.indexOf('ship') > -1) {
          const billProperty = `bill${dataProperty.substr(4)}`

          if(state.data[dataProperty]) {
            state.data[billProperty] = state.data[dataProperty]
          } else {
            state.data[billProperty] = initialState.data[billProperty]
          }
        }
      })

      if(!state.data.phone) {
        state.data.phone = state.previousPhone
      }
    },
    resetShip(state) {
      Object.keys(state.data).map(dataProperty => {
        if(dataProperty !== 'shipping_same' && dataProperty.indexOf('ship') > -1) {
          state.data[dataProperty] = initialState.data[dataProperty]
        }
      })
    },
    resetBill(state) {
      Object.keys(state.data).map(dataProperty => {
        if(dataProperty !== 'shipping_same' && dataProperty.indexOf('bill') > -1) {
          state.data[dataProperty] = initialState.data[dataProperty]
        }
      })

      state.billCountry = ''
      state.billStateOptions = ''

      state.previousPhone = state.data.phone
      state.data.phone = ''
    },
    selectBillCountry(state, action) {
      const {event} = action.payload
  
      if(!state.data.bill_country || event.value != state.data.bill_country.value) { // If the country has changed
        // Since the country is changing via the interface, deselect/clear the state
        state.data.bill_state = ''
    
        state.data[action.payload.action.name] = event.code
    
        state.billCountry = event

        if(state.billCountry.hasOwnProperty('states')) {
          state.billStateOptions = state.billCountry.states
        }
      }
    },
    selectShipCountry(state, action) {
      const {event} = action.payload
  
      if(!state.data.ship_country || event.value !== state.data.ship_country.value) { // If the country has changed
        // Since the country is changing via the interface, deselect/clear the state
        state.data.ship_state = ''
        
        state.data[action.payload.action.name] = event.code
        
        state.shipCountry = event

        if(state.shipCountry.hasOwnProperty('states')) {
          state.shipStateOptions = state.shipCountry.states
        }
      }
    },
    selectState(state, action) {
      const {event} = action.payload

      state.data[action.payload.action.name] = event.value
    },
    paypalCustomerInfo(state, action) {
      state.status = ''
      state.data.shipping_same = true
      state.data.bill_country = action.payload.bill_country
      state.data.ship_country = action.payload.bill_country

      state.billCountry = state.countryOptions.find(({code}) => code === state.data.bill_country)
      if(state.billCountry && state.billCountry.hasOwnProperty('states')) {
        state.billStateOptions = state.countryOptions.find(({code}) => code === state.data.bill_country).states
      }

      state.shipCountry = state.countryOptions.find(({code}) => code === state.data.ship_country)
      if(state.shipCountry && state.shipCountry.hasOwnProperty('states')) {
        state.shipStateOptions = state.countryOptions.find(({code}) => code === state.data.ship_country).states
      }

      Object.keys(action.payload).map((property) => {
        if(state.data.hasOwnProperty(property) && ((property === 'email' && !state.data.email) || !state.data[property])) {
          if(property.indexOf('bill_') !== -1){
            const shipProperty = `ship${property.substr(4)}`

            state.data[property] = action.payload[property]
            state.data[shipProperty] = action.payload[property]
          } else {
            state.data[property] = action.payload[property]
          }
        }
      })
    }
  },
  extraReducers: {
    // Set up the country and state options based on campaign data from get_order
    [fetchOrder.fulfilled]: (state, action) => {
      const {countries_with_states} = action.payload

      state.countryOptions = Object.keys(countries_with_states).map((countryId) => ({
          value: countryId,
          label: countries_with_states[countryId].label,
          states: countries_with_states[countryId].states,
          code: countries_with_states[countryId].code
        })
      )

      if(!action.payload.shipping_information_required) {
        state.data.shipping_same = true
      }
    },
    // Generate test customer information
    [generateTestInfo.pending]: (state) => {
      state.status = 'loading'
    },
    [generateTestInfo.fulfilled]: (state, action) => {
      state.status = ''
      state.data.shipping_same = true
      state.data.bill_country = 'US'
      state.data.ship_country = 'US'

      state.billCountry = state.countryOptions.find(({code}) => code === state.data.bill_country)
      if(state.countryOptions.find(({code}) => code === state.data.bill_country).hasOwnProperty('states')) {
        state.billStateOptions = state.countryOptions.find(({code}) => code === state.data.bill_country).states
      }

      state.shipCountry = state.countryOptions.find(({code}) => code === state.data.ship_country)
      if(state.countryOptions.find(({code}) => code === state.data.ship_country).hasOwnProperty('states')) {
        state.shipStateOptions = state.countryOptions.find(({code}) => code === state.data.ship_country).states
      }

      Object.keys(action.payload).map((property) => {
        if(state.data.hasOwnProperty(property)) {
          if(property.indexOf('bill_') !== -1) {
            const shipProperty = `ship${property.substr(4)}`

            state.data[property] = action.payload[property]
            state.data[shipProperty] = action.payload[property]
          } else {
            state.data[property] = action.payload[property]
          }
        }
      })
    },
    [generateTestInfo.rejected]: (state, action) => {
      state.status = 'failed'
      if(action.payload) {
        state.error = action.payload.message
      } else {
        state.error = action.error.message
      }
    },
    // Fetch customer information from created order
    [fetchCustomerInfo.pending]: (state) => {
      state.status = 'loading'
    },
    [fetchCustomerInfo.fulfilled]: (state, action) => {
      state.status = 'succeeded'
      state.data = {...state.data, ...action.payload}

      if(state.data.bill_country) {
        state.billCountry = state.countryOptions.find(({code}) => code === state.data.bill_country)
        if(state.billCountry && state.billCountry.hasOwnProperty('states')) {
          state.billStateOptions = state.countryOptions.find(({code}) => code === state.data.bill_country).states
        }
      }

      if(state.data.ship_country) {
        state.shipCountry = state.countryOptions.find(({code}) => code === state.data.ship_country)
        if(state.shipCountry && state.shipCountry.hasOwnProperty('states')) {
          state.shipStateOptions = state.countryOptions.find(({code}) => code === state.data.ship_country).states
        }
      }
    },
    [fetchCustomerInfo.rejected]: (state, action) => {
      state.status = 'failed'
      if(action.payload) {
        state.error = action.payload.message
      } else {
        state.error = action.error.message
      }
    },
    // Set the status as succeeded on doAdd
    [addOrder.fulfilled]: (state, action) => {
      if(action.payload.customers_address_shipping_id) {
        state.status = 'succeeded'
      }
    },
    // Reset status when changing the order to run doAdd again after form validation
    'discounts/removeDiscount': (state) => {
      state.status = ''
    },
    'discounts/removeGiftCard': (state) => {
      state.status = ''
    },
    'cart/removeOrderOfferByID': (state) => {
        state.status = ''
    },
    'cart/removeOfferByKey': (state) => {
        state.status = ''
    }
  }
})

export const {
    changeShippingSame,
    changeCustomerInfo,
    changeCustomerInfoStatus,
    shipToBill,
    billToShip,
    resetBill,
    resetShip,
    selectBillCountry,
    selectShipCountry,
    selectState,
    paypalCustomerInfo
  } = customerInfoSlice.actions

export default customerInfoSlice.reducer


// WEBPACK FOOTER //
// ./client/resources/js/checkout/redux/slices/customerInfoSlice.js